import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import { mountBodyOverlay } from "@/ui/domHelpers";

interface BuildingDef {
  id: "forge" | "tavern" | "guild" | "temple";
  icon: string;
  name: string;
  description: string;
  action: (app: GameApp) => void;
}

const BUILDINGS: BuildingDef[] = [
  { id: "forge",  icon: "🔨", name: "Кузница", description: "Улучши карту до сильной версии.",   action: (a) => a.openForge() },
  { id: "tavern", icon: "🍺", name: "Таверна", description: "Купи карту своего домена.",          action: (a) => a.openTavern() },
  { id: "guild",  icon: "🏛", name: "Гильдия", description: "Потрать репутацию на апгрейды.",     action: (a) => a.openGuild() },
  { id: "temple", icon: "⛪", name: "Храм",    description: "Сбрось страх или получи бафф.",     action: (a) => a.openTemple() },
];

export class TownScene extends Phaser.Scene {
  private app!: GameApp;
  private bodyOverlay?: { root: HTMLElement; destroy(): void };
  private root?: HTMLElement;
  private unsubWorld?: () => void;
  private onWake = () => this.refreshResources();

  constructor() {
    super({ key: "TownScene" });
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.setZoom(1);
    this.mount();
    this.refreshResources();
    this.unsubWorld = this.app.onWorldState(() => {
      if (this.scene.isActive()) this.refreshResources();
    });
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdown());
  }

  private mount() {
    const disabled = new Set<string>(["guild"]);
    const buildingsHtml = BUILDINGS.map(
      (b) => {
        const isDisabled = disabled.has(b.id);
        const cls = `town__card${isDisabled ? " town__card--disabled" : ""}`;
        const cta = isDisabled ? "Скоро" : "Войти";
        return `
          <button class="${cls}" data-building="${b.id}" ${isDisabled ? "disabled" : ""}>
            <span class="town__card-icon">${b.icon}</span>
            <h3 class="town__card-name">${b.name}</h3>
            <p class="town__card-desc">${b.description}</p>
            <span class="town__card-cta">${cta}</span>
          </button>
        `;
      },
    ).join("");

    const html = `
      <div class="town">
        <div class="town__bg"></div>
        <div class="town__shade"></div>

        <header class="town__header">
          <div class="town__title-wrap">
            <h1 class="dom-panel__title">Город</h1>
            <p class="dom-panel__subtitle">Где герои отдыхают и готовятся</p>
          </div>
          <button class="dom-btn town__back" data-action="back">← На карту</button>
        </header>

        <main class="town__grid">${buildingsHtml}</main>

        <footer class="town__resources">
          <span>HP <strong data-res="hp">0/0</strong></span>
          <span><span class="icon-gold"></span> <strong data-res="gold">0</strong></span>
          <span>Репутация <strong data-res="reputation">0</strong></span>
          <span>Страх <strong data-res="fear">0/3</strong></span>
        </footer>
      </div>
    `;

    this.bodyOverlay = mountBodyOverlay(this, html);
    this.root = this.bodyOverlay.root;

    this.root.querySelector('[data-action="back"]')?.addEventListener("click", () => this.app.backToMap());

    this.root.querySelectorAll<HTMLButtonElement>(".town__card").forEach((card) => {
      if (card.disabled) return;
      card.addEventListener("click", () => {
        const id = card.dataset.building as BuildingDef["id"];
        const def = BUILDINGS.find((b) => b.id === id);
        def?.action(this.app);
      });
    });
  }

  private refreshResources() {
    if (!this.root) return;
    const hero = this.app.getWorldState()?.hero;
    if (!hero) return;
    const gold = hero.inventory.resources.gold ?? 0;
    const rep = hero.reputation ?? 0;
    const fear = hero.fearAccumulated ?? 0;
    const set = (key: string, value: string) => {
      const el = this.root!.querySelector(`[data-res="${key}"]`) as HTMLElement | null;
      if (el) el.textContent = value;
    };
    set("hp", `${hero.hp}/${hero.maxHp}`);
    set("gold", String(gold));
    set("reputation", String(rep));
    set("fear", `${fear}/3`);
  }

  shutdown() {
    this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
    this.unsubWorld?.();
    this.bodyOverlay?.destroy();
    this.bodyOverlay = undefined;
    this.root = undefined;
  }
}
